/**
 * The page chrome every redesigned screen shares, lifted out of `pages/orders/Detail.tsx` so a
 * header or a card title looks the same on every screen instead of being redrawn per page:
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/profile/profile-header.tsx
 *   `PageHero` — the icon tile and summary strip; its title goes to the sticky header via `PageTitle`;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/tracking-overview.tsx
 *   `CopyButton` — copy-the-number, on the platform clipboard instead of the template's hook;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/shipping-info.tsx
 *   `CardTitle` and `InfoBlock` — the coloured icon tile beside a heading or a label/value pair.
 */
import { useState, type ReactNode } from "react";
import { PiArrowLeftBold as ArrowLeft, PiCheckBold as Check, PiCopySimpleBold as CopyIcon } from "react-icons/pi";
import { ActionIcon, Badge, Button, Text, Title, Tooltip } from "rizzui";
import toast from "react-hot-toast";
import { BackLink } from "../lib/navigation";
import { Breadcrumb, PageTitle, cn, getStatusBadge } from "@steelyard/theme";

/**
 * A screen's header. The title (with its copy button) and the breadcrumb portal into the app's
 * sticky top header through `PageTitle` — the same slot the dashboard's `PageHeader` and every
 * `ListHeader` use — so no screen draws its own title block. What stays in the page is a summary
 * card: the screen's icon tile, a one-line description, its status pills and its actions. When a
 * screen has none of those, nothing renders in the page at all. `children` renders full-width under
 * the card — a detail page's stat strip, for example.
 */
export function PageHero({
  breadcrumb,
  back,
  icon,
  iconClassName = "bg-secondary",
  title,
  copyValue,
  description,
  meta,
  actions,
  children,
}: {
  breadcrumb: { name: string; href?: string }[];
  back?: { href: string; label: string };
  icon: ReactNode;
  /** The tile's solid colour — a `bg-*` token class. White icon on top. */
  iconClassName?: string;
  title: ReactNode;
  /** When set, a copy button sits beside the title. */
  copyValue?: string;
  description?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  const hasBody = Boolean(description || meta || actions || back);
  return (
    <>
      <PageTitle>
        <div className="min-w-0">
          <div className="mb-1.5 flex min-w-0 items-center gap-1.5">
            <Title as="h2" className="truncate text-xl" title={typeof title === "string" ? title : undefined}>
              {title}
            </Title>
            {copyValue ? <CopyButton value={copyValue} label="Copy to clipboard" /> : null}
          </div>
          <Breadcrumb separator="" separatorVariant="circle" className="flex-wrap">
            {breadcrumb.map((item) => (
              <Breadcrumb.Item key={item.name} {...(item.href ? { href: item.href } : {})}>
                {item.name}
              </Breadcrumb.Item>
            ))}
          </Breadcrumb>
        </div>
      </PageTitle>

      {hasBody || children ? (
        <section className="@container overflow-hidden rounded-2xl border border-muted bg-gray-0 dark:bg-gray-50">
          {hasBody ? (
            <div className="flex flex-col gap-4 bg-gradient-to-r from-secondary/10 via-blue/5 to-transparent px-5 py-5 @3xl:px-7 @5xl:flex-row @5xl:items-center @5xl:justify-between">
              <div className="flex min-w-0 items-start gap-4">
                <span className={cn("flex size-14 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm [&>svg]:size-7", iconClassName)}>{icon}</span>
                <div className="flex min-w-0 flex-col gap-2.5 pt-0.5">
                  {description ? <Text className="max-w-2xl text-sm text-gray-600">{description}</Text> : null}
                  {meta ? <div className="flex flex-wrap items-center gap-2">{meta}</div> : null}
                </div>
              </div>
              {actions || back ? (
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  {back ? (
                    <BackLink to={back.href}>
                      <Button as="span" variant="text" size="sm" className="whitespace-nowrap">
                        <ArrowLeft aria-hidden className="me-1.5 size-3.5" />
                        {back.label}
                      </Button>
                    </BackLink>
                  ) : null}
                  {actions}
                </div>
              ) : null}
            </div>
          ) : null}
          {children ? <div className={cn("px-5 py-5 @3xl:px-7", hasBody && "border-t border-muted")}>{children}</div> : null}
        </section>
      ) : null}
    </>
  );
}

/**
 * An edit page's summary strip, in the page above its form: the record's code and current status
 * on the left, links to its own sub-screens (Sellable units, Prices…) on the right. `PageHero`'s
 * card without the icon tile. Never the top bar, and never the form's footer — that bar is Cancel
 * and Save only; the status itself is changed in the form, with its Active checkbox (2026-09-28,
 * the project owner's rule).
 */
export function RecordSummary({ code, meta, links }: { code?: string; meta?: ReactNode; links?: ReactNode }) {
  return (
    <section className="flex flex-col gap-3 rounded-xl border border-muted bg-gray-0 px-5 py-3.5 md:flex-row md:items-center md:justify-between dark:bg-gray-50">
      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
        {code ? (
          <>
            Code
            <Badge variant="flat" color="secondary" rounded="md" className="font-mono">
              {code}
            </Badge>
          </>
        ) : null}
        {meta}
      </div>
      {links ? <div className="flex flex-wrap items-center gap-2">{links}</div> : null}
    </section>
  );
}

/**
 * rizzui's `Dropdown.Trigger` renders its own `<button>`, so it cannot wrap a `Button`. These give
 * it the outline button's look — a labelled menu ("Move to ▾") and a square `⋯` menu.
 */
export const menuTriggerClass =
  "inline-flex h-10 items-center gap-2 rounded-md border border-gray-300 bg-gray-0 px-4 text-sm font-medium transition-colors hover:border-gray-1000 disabled:opacity-60 dark:bg-gray-50";
export const iconMenuTriggerClass =
  "inline-flex size-10 items-center justify-center rounded-md border border-gray-300 bg-gray-0 transition-colors hover:border-gray-1000 disabled:opacity-60 dark:bg-gray-50";

/** `getStatusBadge` in a bordered pill, for a header's meta row. Still the one colour source for a status. */
export function StatusPill({ status }: { status: string }) {
  return <span className="inline-flex items-center rounded-full border border-muted bg-gray-50 px-3 py-1 dark:bg-gray-100">{getStatusBadge(status)}</span>;
}

/** A card heading with its coloured icon tile. `tile` is a `bg-*-lighter text-*-dark` pair, which flips correctly in dark mode. */
export function CardTitle({ icon, tile, children }: { icon: ReactNode; tile: string; children: ReactNode }) {
  return (
    <span className="flex items-center gap-3">
      <span className={cn("flex size-10 shrink-0 items-center justify-center rounded-xl", tile)}>{icon}</span>
      {children}
    </span>
  );
}

export function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip content={copied ? "Copied" : label} placement="top" size="sm" color="invert">
      <ActionIcon
        variant="text"
        size="sm"
        aria-label={label}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            toast.success(`Copied ${value}`);
            setTimeout(() => setCopied(false), 2000);
          } catch {
            toast.error("The browser refused clipboard access");
          }
        }}
      >
        {copied ? <Check aria-hidden className="size-4 text-green-dark" /> : <CopyIcon aria-hidden className="size-4 text-gray-500" />}
      </ActionIcon>
    </Tooltip>
  );
}

/** An uppercase label and its value beside a small coloured icon tile, with an optional action on the label row. */
export function InfoBlock({ icon, tile, label, action, children }: { icon: ReactNode; tile: string; label: string; action?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <span className={cn("mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg", tile)}>{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="flex min-h-7 items-center justify-between gap-2">
          <Text className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</Text>
          {action}
        </div>
        <div className="mt-0.5 flex flex-col">{children}</div>
      </div>
    </div>
  );
}
